<script setup>
import {reactive, onMounted, watch, ref} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";
import {MathHelper} from "@/Utils/MathHelper";

import PDFDocument from '@/StandaloneJS/pdfkit.standalone'
import blobStream from "blob-stream";
import {useDisplay} from "vuetify";

const compName = 'InvoicesComponent';

const { width } = useDisplay()

const props = defineProps({
  contextSchemaA: String,
  contextIdA: String
});

const data = reactive({
  confirmInvoice: false,
  invState: 'Final',
  invoiceDatetitle: true,
  datePickerClosed: false,
  disableAddInvoiceDateBtn: false,
  expanded: [],
  headers: [
    {
      title: 'Invoice #',
      align: 'left',
      key: 'invoicenumber',
    },
    { title: 'Invoice Total', key: 'total' },
    { title: 'Payment Total', key: 'totalPayments' },
    { title: 'Pending Balance', key: 'balance' },
    { title: 'Date', key: 'date' },
    { title: 'Collection Notes', key: 'collection_notes' },
    { title: 'Actions', key: 'actions' },
    { title: '', key: 'data-table-expand' },
  ],
  invoiceDateText: true,
  invoiceDateForm: false,
  // invoiceDateModel: null,
  invoiceDateModelMax: new Date().toLocaleDateString("en-US", { timeZone: "America/New_York" }),
  invoiced: [],
  invoiceItems: [],
  isDisabledAddExpense: false,
  isDisabledGenerateInvoice: false,
  lines: [],
  loading: false,
  claimInformation: [],
  claimant: null,
  claimState: null,
  claimStatePreview: null,
  newII: {
    id: null,
    amount: null,
    quantity: null,
    description: null,
    comment: null
  },
  newInv: false,
  newItem: false,
  payments: [],
  pending: [],
  pendingTask: null,
  showButtons: false,
  singleExpand: true,
  valid: true,
  editingInvoice: null,
  editedInvoice: {},
  lines2: []
});

let invoiceDateModel = ref(new Date().toLocaleDateString("en-US", { timeZone: "America/New_York" }));

watch(invoiceDateModel, (newValue,oldValue)=>{
  //enforce mm/dd/yyyy format since v-date-picker wants to change the output to RFC 2822
  const regex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{4}$/;
  if(!regex.test(newValue)){
    invoiceDateModel.value = formatDate(newValue);
  }
})

const formatDate = (date) => {
  return `${(date.getMonth() + 1)}/${date.getDate()}/${date.getFullYear()}`
}

const a = document.createElement('a')
document.body.appendChild(a)
a.style = "display: none";

const saveCollectionNotes = () => {
  data.loading = true
  ApiWrapper.post('invoices', 'update-collection-note', {
        item: data.editedInvoice
      })
      .then((response) => {
        getData()
        ToastUtil.successMsg('Updated collection note');
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.loading = false;
      })
}

const editInvoice = (item) => {
  data.editingInvoice = true
  data.editedInvoice = JSON.parse(JSON.stringify(item))
}

const itemRowBackground = (item) => {
  item = item.item;
  let rowClass = '';

  if (item.total !== '$0.00') {
    if(item.paid === true) {
      if(item.balance === 0 || (item.balance && MathHelper.round(item.balance,2) === 0)) {
        rowClass = 'style-1';
      }
      else {
        rowClass = 'style-3'
      }
    }
    else if(item.paid === false) {
      rowClass = 'style-2';
    }
  }

  return {
    class: rowClass
  }
}

const generateInvoiceCurrentDate = () => {
  if (data.pending.length > 0) {
    data.isDisabledGenerateInvoice = true
    data.loading = true
    ApiWrapper.post('time-expense', 'invoice-items', {
          date: new Date(),
          claimId: props.contextIdA,
          invState: data.invState
        })
        .then((response) => {
          getData();
          activeComponentStore.notifyComponents(['invoices','disbursements','disbursementrules','claims','updateTasks'],compName);
          ToastUtil.successMsg('Generated invoice');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.isDisabledGenerateInvoice = false;
        })
        .finally(() => {
          data.loading = false;
        })
  } else {
    ToastUtil.errorMsg('No Invoice Items To Invoice')
  }
}

const generateInvoice = () => {
  if (data.pending.length > 0) {
    data.isDisabledGenerateInvoice = true
    data.loading = true
    ApiWrapper.post('time-expense', 'invoice-items', {
          date: invoiceDateModel.value,
          claimId: props.contextIdA,
          invState: data.invState
        })
        .then((response) => {
          getData();
          activeComponentStore.notifyComponents(['invoices','disbursements','disbursementrules','claims','updateTasks'],compName);
          ToastUtil.successMsg('Generated invoice');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.isDisabledGenerateInvoice = false;
        })
        .finally(() => {
          data.loading = false;
        })
  } else {
    ToastUtil.errorMsg('No Invoice Items To Invoice')
  }
}

const closeClaim = () => {
  data.loading = true;
  ApiWrapper.post('time-expense', 'close-claim', {
        claimId: props.contextIdA
      })
      .then((response) => {
        activeComponentStore.notifyComponents(['claims'],compName);

        if(response.message === 'Pending Task/Invoice Item'){
          ToastUtil.errorMsg('Failed to close claim. Reason: Pending Task Or Time/Expense Item')
        }
        else {
          ToastUtil.successMsg('Closed claim');
        }
      })
      .catch((err) => {
        ToastUtil.error(err);
      })
      .finally(() => {
        data.loading = false;
      })
}

const getData = () => {
  data.loading = true
  ApiWrapper.post('time-expense', 'time-expense', {
        claimId: props.contextIdA
      })
      .then((response) => {
        if(response){
          data.invoiced = response.invoiced
          data.pending = response.pending
          data.pendingTask = response.pendingTask
          data.claimStatePreview = response.state
          data.claimInformation = response.claimInformation
          data.claimant = response.claimant
        }
      })
      .catch((err) => {
        ToastUtil.error(err);
      })
      .finally(()=>{
        data.loading = false
        data.isDisabledAddExpense = false
        data.isDisabledGenerateInvoice = false
        data.disableAddInvoiceDateBtn = false;
      })

  ApiWrapper.get('invoices', 'invoices','claimId=' + props.contextIdA)
      .then((response) => {
        data.claimState = response.state
        let tempLines = response.items
        tempLines.forEach(function (arrayItem) {
          arrayItem.date =  new Date(arrayItem.date.replaceAll("-","/")).toLocaleDateString();
        });
        data.client = response.client
        ApiWrapper.post('invoices', 'get-all-payments', {claimId: props.contextIdA})
        .then((response) => {
          data.payments = response.payments;
          let invoiceid = null;
          for (let x in tempLines){
            invoiceid = tempLines[x].invoiceid
            tempLines[x].balance = tempLines[x].totalformatted * 1
            tempLines[x].totalPayments = 0
            for (let y in data.payments) {
              if (data.payments[y].invoiceid === invoiceid && data.payments[y].amountformatted !== null) {
                tempLines[x].totalPayments += data.payments[y].amountformatted * 1
              }
            }
            tempLines[x].balance = (tempLines[x].totalformatted*1) - tempLines[x].totalPayments;
            tempLines[x].totalPayments = '$' + tempLines[x].totalPayments;
          }
          data.lines = tempLines
        })
        .catch((err) => {
          ToastUtil.error(err);
        })
        .finally(() => {
          data.loading = false;
        })
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(() => {
        data.loading = false;
      });

  ApiWrapper.post('invoices', 'get-all-invoice-items', {claimId: props.contextIdA}).then((response) => {
    data.invoiceItems = response.invoiceItems
  })
  .catch((e)=>{
    ToastUtil.error(e);
  })
}

const getInvoiceItems = (item) => {
  data.loading = true
  ApiWrapper.post('invoices', 'get-invoice-items-x', {
        invoiceid: item.invoiceid,
      })
      .then((response) => {
         let invoiceitems = response.invoiceItems;
          let sum = response.sum;
          download(item, invoiceitems, sum);
          data.loading = false;
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.loading = false;
      })
}

const deleteItem = (item) => {
   let deleteMessage = confirm(
      'Are you sure you want to void this invoice?'
  )

  if (deleteMessage === true) {
    data.loading = true;
    ApiWrapper.post('invoices', 'void-invoice', {
          invoiceid: item.invoiceid
        })
        .then((response) => {
          if(response.message === 'Paid'){
            ToastUtil.errorMsg('Cannot void: Invoice has been paid.')
          }
          else {
            ToastUtil.successMsg('Voided invoice');
          }
          getData();
          activeComponentStore.notifyComponents(['disbursements'],compName);
        })
        .catch((err) => {
          ToastUtil.error(err);
        })
        .finally(() => {
          data.loading = false;
        });
  }
}

const toDataURL = (url, callback) => {
  let xhr = new XMLHttpRequest();
  xhr.onload = function() {
    let reader = new FileReader();
    reader.onloadend = function() {
      callback(reader.result);
    }
    reader.readAsDataURL(xhr.response);
  };
  xhr.open('GET', url);
  xhr.responseType = 'blob';
  xhr.send();
}

const download = (item, invoiceitems, sum) =>  {
  var doc = new PDFDocument({ margin: 25 })
  var stream = doc.pipe(blobStream())

  // doc
  //   .font('Times-Bold')
  //   .fontSize(12)
  //   .text('141 Quaker Way', 80, 23)
  // doc
  //   .font('Times-Bold')
  //   .fontSize(12)
  //   .text('Knoxville TN 37920', 80, 37)

  var c = document.createElement('canvas');
  var img = document.getElementById('tux');
  c.height = img.naturalHeight;
  c.width = img.naturalWidth;
  var ctx = c.getContext('2d');

  ctx.drawImage(img, 0, 0, c.width, c.height);
  var base64String = c.toDataURL();
  doc.image(base64String, 25, 10, {width: 150})
  doc
      .font('Times-Bold')
      .fontSize(8)
      .text('Associated Adjusting, LLC', { align: 'right' }, 25)
  // doc
  //     .font('Times-Roman')
  //     .fontSize(8)
  //     .text('137 East Young High Pike', { align: 'right' })
  doc
      .font('Times-Roman')
      .fontSize(8)
      .text('PO BOX 9119', { align: 'right' })
  doc
      .font('Times-Roman')
      .fontSize(8)
      .text('Knoxville TN, 37920', { align: 'right' })


  //   doc
  //   .font('Times-Bold')
  //   .fontSize(8)
  //   .text(data.client.name, { align: 'right' }, 25)
  // doc
  //   .font('Times-Roman')
  //   .fontSize(8)
  //   .text(data.client.streetaddress, { align: 'right' })
  // doc
  //   .font('Times-Roman')
  //   .fontSize(8)
  //   .text(data.client.city + ', ' + data.client.state + ' ' +  data.client.zip, { align: 'right' })

  doc.moveTo(25, 75).lineTo(587, 75).stroke()

  doc.font('Times-Roman').fontSize(10).text('Invoice Date: ', 50, 80)
  doc.moveUp()
  doc.font('Times-Roman').fontSize(10).text(item.date, 150)
  doc.moveUp()
  doc.font('Times-Roman').fontSize(10).text('Amount: ', 350)
  doc.moveUp()
  doc.font('Times-Roman').fontSize(10).text(item.total, 450)

  doc.font('Times-Roman').fontSize(10).text('Claim Number: ', 50)
  doc.moveUp()
  if(item.claimnumber){
    doc.font('Times-Roman').fontSize(10).text(item.claimnumber, 150)
    doc.moveUp()
  }
  doc.font('Times-Roman').fontSize(10).text('Invoice Number: ', 350)
  doc.moveUp()
  doc.font('Times-Roman').fontSize(10).text(item.invoicenumber, 450)

  doc.font('Times-Roman').fontSize(10).text('Tax Id Number: ', 350)
  doc.moveUp()
  doc.font('Times-Roman').fontSize(10).text('26-1629169', 450)

  doc
      .moveTo(25, doc.y + 5)
      .lineTo(587, doc.y + 5)
      .stroke()

  doc.font('Times-Bold').fontSize(10).text('Invoice To: ' + data.client.name, 50, 160)
  doc.font('Times-Roman').fontSize(10).text(data.client.streetaddress, 50, 170)
  doc.font('Times-Roman').fontSize(10).text(data.client.city + (data.client.city ? ', ' : '') + data.client.state + ' ' + data.client.zip, 50, 180)


  // doc.font('Times-Roman').fontSize(10).text('Insured: ', 350, 160)
  // doc.font('Times-Roman').fontSize(10).text(item.insured, 425, 160)

  //handle case of insured, claimant, or both and prevent same line overwrite
  if(data.claimInformation.insured){
    doc.font('Times-Roman','normal').fontSize(10).text('Insured: ', 350, 160)
    doc.font('Times-Roman','normal').fontSize(10).text(data.claimInformation.insured, 425, 160)
    if(data.claimant){
      doc.font('Times-Roman','normal').fontSize(10).text('Claimant: ', 350, 170)
      doc.font('Times-Roman','normal').fontSize(10).text(data.claimant, 425, 170)
    }
  }
  else{
    if(data.claimant){
      doc.font('Times-Roman','normal').fontSize(10).text('Claimant: ', 350, 160)
      doc.font('Times-Roman','normal').fontSize(10).text(data.claimant, 425, 160)
    }
  }

  doc.font('Times-Roman').fontSize(10).text('Adjuster: ', 350, 180)
  doc.font('Times-Roman').fontSize(10).text(item.adjuster, 425, 180)

  doc
      .font('Times-Roman')
      .fontSize(10)
      .text('Policy Number: ', 350, 190)
  doc.font('Times-Roman').fontSize(10).text(item.policynumber, 425, 190)

  doc
      .font('Times-Roman')
      .fontSize(10)
      .text('Claim Number: ', 350, 200)
  doc.font('Times-Roman').fontSize(10).text(item.claimnumber, 425, 200)



  doc
      .font('Times-Bold')
      .fontSize(20)
      .text('TOTAL AMOUNT DUE: ', 50, 250)
  doc.font('Times-Roman').fontSize(20).text(item.total, 400, 250)

  doc
      .font('Times-Bold')
      .fontSize(12)
      .text('Invoice Details: ', 50, 300)

  doc.moveTo(50, 310).lineTo(562, 310).stroke()
  doc.moveDown()

  if(data.claimState == 'tx' || data.claimState == 'texas'){
    if((data.client.id * 1) != 154 && (data.client.id * 1) != 185){
      let taxes = {}
      //taxes.total = Math.round((item.totalformatted - (item.totalformatted / 1.0825)) * 100) / 100
      taxes.total = Math.round((item.totalformatted * 1 - sum * 1) * 100) / 100
      taxes.description = 'Taxes : TX';
      taxes.date = item.date
      taxes.quantity = 1
      //taxes.amount = Math.round((item.totalformatted - (item.totalformatted / 1.0825)) * 100) / 100
      taxes.amount = Math.round((item.totalformatted * 1 - sum * 1) * 100) / 100
      invoiceitems.push(taxes)
    }
  }

  let salesTaxStates = ['hi','nm','sd','wv'];

  if(salesTaxStates.includes(data.claimState.toLowerCase())){
    let taxes = {}
    //taxes.total = Math.round((item.totalformatted - (item.totalformatted / 1.0825)) * 100) / 100
    taxes.total = Math.round((item.totalformatted * 1 - sum * 1) * 100) / 100
    taxes.description = 'Taxes : ' + data.claimState.toUpperCase();
    taxes.date = item.date
    taxes.quantity = 1
    //taxes.amount = Math.round((item.totalformatted - (item.totalformatted / 1.0825)) * 100) / 100
    taxes.amount = Math.round((item.totalformatted * 1 - sum * 1) * 100) / 100
    invoiceitems.push(taxes)
  }


  for (let ii in invoiceitems) {
    if (ii == 0) {
      doc
          .font('Times-Bold')
          .fontSize(12)
          .text(invoiceitems[ii].description, 50)
    } else if (
        invoiceitems[ii].description != invoiceitems[ii - 1].description
    ) {

      doc
          .moveTo(350, doc.y + 2)
          .lineTo(562, doc.y + 2)
          .stroke()

      doc.moveDown()
      doc.moveDown()
      doc
          .font('Times-Bold')
          .fontSize(12)
          .text(invoiceitems[ii].description, 50)
    }
    if(invoiceitems[ii].comment){
      doc
          .font('Times-Roman')
          .fontSize(8)
          .text(invoiceitems[ii].comment, 50)
    }

    doc.moveUp()
    doc
        .font('Times-Roman')
        .fontSize(10)
        .text(invoiceitems[ii].total, 400)
    doc
        .font('Times-Roman')
        .fontSize(8)
        .text(
            'Billed for ' +
            invoiceitems[ii].quantity +
            ' at ' +
            invoiceitems[ii].amount +
            ' each.',
            50
        )
    doc.moveDown()
    if (ii == invoiceitems.length - 1) {

      doc
          .moveTo(350, doc.y + 2)
          .lineTo(562, doc.y + 2)
          .stroke()
    }
    doc.moveDown()
    doc.moveDown()
  }

  doc
      .font('Times-Bold')
      .fontSize(12)
      .text('TOTAL INVOICE AMOUNT: ', 50)
  doc.moveUp()
  doc.font('Times-Bold').fontSize(12).text(item.total, 500)

  doc
      .moveTo(562, doc.y + 3)
      .lineTo(50, doc.y + 3)
      .stroke()

  doc.moveDown()
  doc
      .font('Times-Roman')
      .fontSize(8)
      .text('All Invoices are due and payable upon receipt', 0, doc.y, {
        align: 'center',
      })
  doc
      .font('Times-Roman')
      .fontSize(8)
      .text(
          'We reserve the right to assess a service charge of 1.5% per month (18% per year) for all past due balances.',
          { align: 'center' }
      )

  doc.moveDown()
  doc.moveDown()

  doc
      .font('Times-Bold')
      .fontSize(12)
      .text('Thank you for your business!', { align: 'center' })

  doc.end()

  var blob
  stream.on('finish', function () {
    blob = stream.toBlob('application/pdf')
    if (!blob) return
    var url = window.URL.createObjectURL(blob)
    a.href = url

    a.download = item.invoicenumber
    a.click()
    window.URL.revokeObjectURL(url)
  })
}

const prepPreview = (item) => {
  ApiWrapper.post('invoices', 'get-invoice-number', {
        claimId: props.contextIdA
      })
      .then((response) => {
        downloadPreview(item, response.invoiceNumber);
      })
      .catch((err) => {
        ToastUtil.error(err);
      })
}

const downloadPreview = (item, invoicenumber) => {

  let totalAmount = data.pending.reduce((a, {total}) => a + Number(total), 0);

  // Get today's date to put on preview PDF
  let today = new Date();
  let dd = String(today.getDate()).padStart(2, '0');
  let mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0
  let yyyy = today.getFullYear();
  today = mm + '/' + dd + '/' + yyyy;
  // --------------------------------------

   let doc = new PDFDocument({ margin: 25 })

   let stream = doc.pipe(blobStream())

   let c = document.createElement('canvas');
   let img = document.getElementById('tux');
  c.height = img.naturalHeight;
  c.width = img.naturalWidth;
   let ctx = c.getContext('2d');

  ctx.drawImage(img, 0, 0, c.width, c.height);
   let base64String = c.toDataURL();

  doc.image(base64String, 25, 10, {width: 150})

  if(data.claimStatePreview === 'TX' || data.claimStatePreview === 'tx' || data.claimStatePreview === 'texas'){
    if((data.client.id * 1) !== 154 && (data.client.id * 1) !== 185){
      let taxes = {}
      taxes.total = (Math.round(((totalAmount * 0.0825) + Number.EPSILON) * 100) / 100).toFixed(2)
      taxes.description = 'Taxes : TX';
      taxes.date = today
      taxes.quantity = 1
      taxes.amount = (Math.round(((totalAmount * 0.0825) + Number.EPSILON) * 100) / 100).toFixed(2)
      totalAmount = totalAmount + (taxes.total) * 1
      totalAmount = (Math.round((totalAmount + Number.EPSILON) * 100) / 100).toFixed(2)
      data.pending.push(taxes)
    }else{
      totalAmount = totalAmount.toFixed(2)
    }
  }else{
    totalAmount = totalAmount.toFixed(2)
  }

  let salesTaxStates = ['hi','nm','sd','wv'];

  if(salesTaxStates.includes(data.claimStatePreview.toLowerCase())){
    let taxRate = 0;
    switch(data.claimStatePreview.toLowerCase()){
      case 'hi':
        taxRate = 0.04;
        break;
      case 'nm':
        taxRate = 0.05125;
        break;
      case 'sd':
        taxRate = 0.065;
        break;
      case 'wv':
        taxRate = 0.06;
        break;
    }
    if(taxRate > 0){
      totalAmount = parseFloat(totalAmount);
      let taxes = {}
      taxes.total = (Math.round(((totalAmount * taxRate) + Number.EPSILON) * 100) / 100).toFixed(2)
      taxes.description = 'Taxes : ' + data.claimStatePreview.toUpperCase();
      taxes.date = today
      taxes.quantity = 1
      taxes.amount = (Math.round(((totalAmount * taxRate) + Number.EPSILON) * 100) / 100).toFixed(2)

      totalAmount = totalAmount + (taxes.total) * 1
      totalAmount = (Math.round((totalAmount + Number.EPSILON) * 100) / 100).toFixed(2)
      data.pending.push(taxes)
    }
  }


  doc
      .fillColor('black') // Turns rest of document color back to black after 'Pending'
      .font('Times-Roman','bold')
      .fontSize(8)
      .text('Associated Adjusting, LLC', { align: 'right' }, 25)
  // doc
  //     .font('Times-Roman','normal')
  //     .fontSize(8)
  //     .text('137 East Young High Pike', { align: 'right' })
  doc
      .font('Times-Roman','normal')
      .fontSize(8)
      .text('PO BOX 9119', { align: 'right' })
  doc
      .font('Times-Roman','normal')
      .fontSize(8)
      .text('Knoxville TN, 37920', { align: 'right' })

  doc.moveTo(25, 75).lineTo(587, 75).stroke()

  doc.font('Times-Roman','normal').fontSize(10).text('Invoice Date: ', 50, 80)
  doc.moveUp()
  doc.font('Times-Roman','normal').fontSize(10).text(invoiceDateModel.value, 150)
  doc.moveUp()
  doc.font('Times-Roman','normal').fontSize(10).text('Amount: ', 350)
  doc.moveUp()
  doc.font('Times-Roman','normal').fontSize(10).text(`$${totalAmount}`, 450)

  doc.font('Times-Roman','normal').fontSize(10).text('Claim Number: ', 50, 90)
  doc.moveUp()

  if(data.claimInformation.claimnumber){
    doc.font('Times-Roman','normal').fontSize(10).text(data.claimInformation.claimnumber, 150, 90)
    doc.moveUp()
  }
  doc.font('Times-Roman','normal').fontSize(10).text('Invoice Number: ', 350)
  doc.moveUp()
  doc.font('Times-Roman','normal').fontSize(10).text(invoicenumber, 450)

  doc.font('Times-Roman','normal').fontSize(10).text('Tax Id Number: ', 350)
  doc.moveUp()
  doc.font('Times-Roman','normal').fontSize(10).text('26-1629169', 450)

  doc
      .moveTo(25, doc.y + 5)
      .lineTo(587, doc.y + 5)
      .stroke()

  doc.font('Times-Roman','bold').fontSize(10).text('Invoice To: ' + data.client.name, 50, 160)
  doc.font('Times-Roman','normal').fontSize(10).text(data.client.streetaddress, 50, 170)
  doc.font('Times-Roman','normal').fontSize(10).text(data.client.city + ', ' + data.client.state + ' ' + data.client.zip, 50, 180)

  //handle case of insured, claimant, or both and prevent same line overwrite
  if(data.claimInformation.insured){
    doc.font('Times-Roman','normal').fontSize(10).text('Insured: ', 350, 160)
    doc.font('Times-Roman','normal').fontSize(10).text(data.claimInformation.insured, 425, 160)
    if(data.claimant){
      doc.font('Times-Roman','normal').fontSize(10).text('Claimant: ', 350, 170)
      doc.font('Times-Roman','normal').fontSize(10).text(data.claimant, 425, 170)
    }
  }
  else{
    if(data.claimant){
      doc.font('Times-Roman','normal').fontSize(10).text('Claimant: ', 350, 160)
      doc.font('Times-Roman','normal').fontSize(10).text(data.claimant, 425, 160)
    }
  }


  doc.font('Times-Roman','normal').fontSize(10).text('Adjuster: ', 350, 180)
  doc.font('Times-Roman','normal').fontSize(10).text(data.claimInformation.adjuster, 425, 180)

  doc
      .font('Times-Roman','normal')
      .fontSize(10)
      .text('Policy Number: ', 350, 190)
  doc.font('Times-Roman','normal').fontSize(10).text(data.claimInformation.policynumber, 425, 190)

  doc
      .font('Times-Roman','normal')
      .fontSize(10)
      .text('Claim Number: ', 350, 200)
  doc.font('Times-Roman','normal').fontSize(10).text(data.claimInformation.claimnumber, 425, 200)


  doc
      .font('Times-Roman','bold')
      .fontSize(20)
      .text('TOTAL AMOUNT DUE: ', 50, 250)
  doc.font('Times-Roman','normal').fontSize(20).text(`$${totalAmount}`, 400, 250)

  doc
      .font('Times-Roman','bold')
      .fontSize(12)
      .text('Invoice Details: ', 50, 300)

  doc.moveTo(50, 310).lineTo(562, 310).stroke()
  doc.moveDown()

  for (let x in data.pending) {
    if (x === 0) {
      doc
          .font('Times-Roman','bold')
          .fontSize(12)
          .text(data.pending[x].description, 50)
    } else if (data.pending[x]?.description !== data.pending[x - 1]?.description) {

      doc.moveDown()
      doc
          .font('Times-Roman','bold')
          .fontSize(12)
          .text(data.pending[x].description, 50)
    }

    if(data.pending[x].comment) {
      doc
          .font('Times-Roman','normal')
          .fontSize(10)
          .text(data.pending[x].comment, 50)
    }

    doc
        .font('Times-Roman','normal')
        .fontSize(10)
        .text(
            'Billed for ' +
            data.pending[x].quantity +
            ' at ' +
            '$' + data.pending[x].amount +
            ' each.',
            50
        )
  }

  doc.moveDown()
  doc.moveDown()

  doc
      .font('Times-Roman','bold')
      .fontSize(12)
      .text('TOTAL INVOICE AMOUNT: ', 50)
  doc.moveUp()

  doc.font('Times-Roman','bold').fontSize(12).text(`$${totalAmount}`, 500)

  doc.moveDown()

  doc
      .moveTo(562, doc.y + 3)
      .lineTo(50, doc.y + 3)
      .stroke()

  doc.moveDown()

  doc
      .font('Times-Roman','normal')
      .fontSize(8)
      .text('All Invoices are due and payable upon receipt', 0, doc.y, {
        align: 'center',
      })
  doc
      .font('Times-Roman','normal')
      .fontSize(8)
      .text(
          'We reserve the right to assess a service charge of 1.5% per month (18% per year) for all past due balances.',
          { align: 'center' }
      )

  doc.moveDown()
  doc.moveDown()

  doc
      .font('Times-Roman','bold')
      .fontSize(12)
      .text('Thank you for your business!', { align: 'center' })

  doc.end()

  let blob;
  stream.on('finish', function () {
    blob = stream.toBlob('application/pdf')
    if (!blob) return
    let url = window.URL.createObjectURL(blob)
    a.href = url
    a.download = data.claimInformation.claimnumber
    window.open(a);
    window.URL.revokeObjectURL(url)
  })
}

onMounted(()=>{
  getData()

  activeComponentStore.registerComponent(
      compName,
      [
        'tasks',
        'disbursements',
        'person',
        'invoices'
      ],
      getData
  );
});
</script>

<template>
  <v-container fluid>
    <v-card>
      <v-toolbar href="" flat color="white">
        <v-toolbar-title>Invoices</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        ></v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>
        <v-spacer></v-spacer>
        <v-btn
            v-if="!data.invoiceDateForm && Permissions.checkPermissions(['claims.close'])"
            size="small"
            variant="elevated"
            color="primary"
            @click="closeClaim()"
        >
          Close Claim
        </v-btn>
      </v-toolbar>
      <!-- <v-row href="" class="justify-center pa-2" v-if="!invoiceDateForm">
        <v-btn
          size="small"
          color="primary"
          @click="closeClaim()"
        >
          Close Claim
        </v-btn> -->

      <!-- If the Close Claim button needs to be hidden under certain conditions use the button with the v-if below. -->
      <!-- <v-btn
        v-if="pending.length === 0 && pendingTask === false"
        size="small"
        color="primary"
        @click="closeClaim()"
      >
        Close Claim
      </v-btn>
    </v-row> -->
      <v-row href="" class="justify-center pa-2 mb-2" v-if="data.pending.length !== 0 && !data.invoiceDateForm">

        <v-btn
            size="small"
            variant="elevated"
            class="success mr-2"
            :disabled="data.disableAddInvoiceDateBtn"
            @click="data.invoiceDateForm = true;data.showButtons = true;"
        >
          Generate Invoice
        </v-btn>
        <!-- <v-btn
          size="small"
          color="primary"
          v-if="Permissions.checkPermissions(['invoices.items'])"
          @click="generateInvoiceCurrentDate(); disableAddInvoiceDateBtn = true"
          :loading="isDisabledGenerateInvoice"
          :disabled="isDisabledGenerateInvoice"
          >Generate Invoice</v-btn
        > -->
      </v-row>

      <div v-if="data.invoiceDateForm" class="justify-center pa-2">
        <v-row justify="center" align="start">
          <v-col
              cols="6"
              class="pb-0"
              v-if="data.invoiceDateText"
          >
            <v-text-field
                v-model="invoiceDateModel"
                label="Select invoice date below"
                prepend-icon="mdi-calendar"
                @click="data.datePickerClosed = true"
                readonly>
            </v-text-field>
            <v-select v-model="data.invState" :items="['Interim', 'Final']" label="Invoice Type"></v-select>
          </v-col>
        </v-row>
        <v-row class="text-center mb-2" align="start">
          <v-col
              cols="12"
              class="pt-0"
          >
            <v-date-picker
                v-if="data.datePickerClosed"
                v-model="invoiceDateModel"
                no-title
                :max="data.invoiceDateModelMax"
                :show-current="true"
                class="elevation-2"
                @input="data.datePickerClosed = false"
            >
              <v-spacer></v-spacer>
              <v-btn
                  variant="text"
                  size="small"
                  color="error"
                  @click="data.datePickerClosed = true; data.invoiceDateForm = false;"
              >Cancel</v-btn
              >
            </v-date-picker>
            <div v-if="data.showButtons && invoiceDateModel !== ''" class="text-center mt-2">

              <v-btn
                  size="small"
                  variant="elevated"
                  class="primary mr-2"
                  v-if="Permissions.checkPermissions(['invoices.preview-pdf'])"
                  @click="prepPreview(data.pending); data.showButtons = false; data.confirmInvoice = Permissions.checkPermissions(['invoices.items']); data.invoiceDateText = false;"
              >
                Preview Invoice PDF
              </v-btn>
              <v-btn
                  size="small"
                  variant="elevated"
                  color="error"
                  @click="data.invoiceDateForm = false;data.invoiceDateText = true;"
              >Cancel</v-btn
              >
            </div>
            <div v-if="data.confirmInvoice">
              <v-row align="text-center" class="px-5 mt-2 mb-5">
                <h2>Would you like to approve and generate this invoice?</h2>
              </v-row>
              <v-row align="center" class="px-5">
                <v-btn
                    size="small"
                    variant="elevated"
                    class="primary mx-2"
                    @click="generateInvoice(); data.confirmInvoice = false; data.disableAddInvoiceDateBtn = true; data.invoiceDateForm = false; data.invoiceDateText = true;"
                >Yes</v-btn
                >
                <v-btn
                    size="small"
                    variant="elevated"
                    class="mx-2"
                    color="error"
                    @click="data.invoiceDateForm = false; data.confirmInvoice = false; data.invoiceDateText = true;"
                >Cancel</v-btn
                >
              </v-row>
            </div>
          </v-col>
        </v-row>
      </div>
      <v-row class="mx-7" v-if="data.editingInvoice === true">
        <v-text-field label="Collection Notes" v-model="data.editedInvoice.note"></v-text-field>
        <v-btn
            size="small"
            variant="elevated"
            class="primary mx-2"
            @click="data.editingInvoice=false; saveCollectionNotes()"
        >Save</v-btn
        >
        <v-btn
            size="small"
            variant="elevated"
            class="mx-2"
            color="error"
            @click="data.editingInvoice = false; data.editedInvoice={}"
        >Cancel</v-btn
        >
      </v-row>
      <v-data-table
          href=""
          :row-props="itemRowBackground"
          :headers="data.headers"
          :items="data.lines"
          hide-default-footer
          class="elevation-1"
          :single-expand="true"
          v-model:expanded="data.expanded"
          item-value="invoicenumber"
          show-expand
          loading-text="Loading... Please wait"
          :items-per-page="-1"
          :mobile="width <= 1700"
      >
        <template v-slot:[`item.totalFormatted`]="{ item }">
          <span>{{ '$' + Number(item.totalFormatted).toFixed(2) }}</span>
        </template>
        <template v-slot:[`item.invoicenumber`]="{ item }">
          <span>{{ item.void_date === null ? item.invoicenumber : '*VOIDED* ' + item.invoicenumber  }}</span>
        </template>
        <template v-slot:[`item.totalPayments`]="{ item }">
          <span>{{ item.totalPayments }}</span>
        </template>
        <template v-slot:[`item.balance`]="{ item }">
          <span>{{ '$' + Number(item.balance).toFixed(2) }}</span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-icon v-if="Permissions.checkPermissions(['invoices.update-collection-note'])" @click="editInvoice(item)"> mdi-pencil </v-icon>
          <v-icon v-if="Permissions.checkPermissions(['invoices.void'])" @click="deleteItem(item)"> mdi-delete </v-icon>
          <v-icon v-if="Permissions.checkPermissions(['invoices.items'])" @click="getInvoiceItems(item)"> mdi-file-pdf-box </v-icon>
        </template>

        <template v-slot:[`expanded-row`]="{ columns, item }">
          <td :colspan="columns.length" class="pb-2 expanded-row">
            <h3 class="pt-2">Invoice Items</h3>
            <v-data-table
                density="compact"
                hide-default-footer
                :items-per-page="-1"
                :mobile="width <= 1700"
            >
              <template v-slot:default>
                <thead>
                <tr>
                  <th>Category</th>
                  <th>Amount</th>
                  <th>Quantity</th>
                  <th>Total</th>
                  <th>Person</th>
                  <th>Comment</th>
                </tr>
                </thead>
                <tbody>
                <tr
                    v-for="ii in data.invoiceItems.filter((invi) => invi.invoiceid === item.invoiceid)"
                    :key="ii"
                >
                  <td v-if="ii.invoiceid === item.invoiceid">{{ ii.category }}</td>
                  <td v-if="ii.invoiceid === item.invoiceid">{{ ii.amount }}</td>
                  <td v-if="ii.invoiceid === item.invoiceid">{{ ii.quantity }}</td>
                  <td v-if="ii.invoiceid === item.invoiceid">{{ ii.total }}</td>
                  <td v-if="ii.invoiceid === item.invoiceid">{{ ii.name }}</td>
                  <td v-if="ii.invoiceid === item.invoiceid">{{ ii.comment }}</td>
                </tr>
                </tbody>
              </template>
            </v-data-table>
            <v-divider></v-divider>
            <h3 class="pt-2">Payments</h3>
            <v-data-table
                density="compact"
                hide-default-footer
                :items-per-page="-1"
                :mobile="width <= 1700"
            >
              <template v-slot:default>
                <thead>
                <tr>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Check #</th>
                  <th>Check Date</th>
                  <th>Batch</th>
                </tr>
                </thead>
                <tbody>
                <tr
                    v-for="pay in data.payments.filter((py) => py.invoiceid === item.invoiceid)"
                    :key="pay"
                >
                  <td v-if="pay.invoiceid === item.invoiceid">{{ pay.amount }}</td>
                  <td v-if="pay.invoiceid === item.invoiceid">{{ pay.date }}</td>
                  <td v-if="pay.invoiceid === item.invoiceid">{{ pay.checknumber }}</td>
                  <td v-if="pay.invoiceid === item.invoiceid">{{ pay.checkdate }}</td>
                  <td v-if="pay.invoiceid === item.invoiceid">{{ pay.title }}</td>

                </tr>
                </tbody>
              </template>
            </v-data-table>
          </td>
        </template>
      </v-data-table>

      <img id="tux"
           crossOrigin="anonymous"
           src="/images/aanew.jpg"
           style="display: none"
           alt=""/>
    </v-card>
  </v-container>
</template>

<style scoped>
.container {
  padding: 5px;
}
[v-cloak] {
  display: none;
}

:deep(*) .style-1 {
  background-color: rgb(159, 252, 159);
}
:deep(*) .style-2 {
  background-color: rgb(253, 148, 148);
}
:deep(*) .style-3 {
  background-color: rgb(253, 253, 120);
}
</style>